// The patient portal's calls (docs/plans/phase3-portal-feedback-kiswahili.md §3.3; ADR 0035).
// The portal session lives in its own httpOnly cookie; the browser sends it with these calls
// and the server checks it on every one. Tokens (the sign-in challenge, the feedback link)
// go in request bodies, never in an API URL.
import {
  feedbackLinkSchema,
  portalAllergiesSchema,
  portalAppointmentsSchema,
  portalBillsSchema,
  portalCodeRequestResultSchema,
  portalFeedbackSchema,
  portalInfoSchema,
  portalMeSchema,
  portalResultsSchema,
  type SubmitFeedbackBody,
  type UiLanguage,
} from '@wodi/shared';
import { queryOptions } from '@tanstack/react-query';
import type { z } from 'zod';

import { api } from './client.ts';

export const portalKeys = {
  info: (code: string) => ['portal', 'info', code] as const,
  me: ['portal', 'me'] as const,
  section: (patientId: string, section: string) => ['portal', patientId, section] as const,
};

export const portalInfoQuery = (code: string) =>
  queryOptions({
    queryKey: portalKeys.info(code),
    queryFn: () =>
      api('GET', `/portal/${encodeURIComponent(code)}/info`, { schema: portalInfoSchema }),
    retry: false,
  });

export const portalMeQuery = queryOptions({
  queryKey: portalKeys.me,
  queryFn: () => api('GET', '/portal/me', { schema: portalMeSchema }),
  retry: false,
});

export function requestPortalCode(body: {
  portalCode: string;
  phone: string;
  language: UiLanguage;
}) {
  return api('POST', '/portal/sign-in/code', { body, schema: portalCodeRequestResultSchema });
}

export function verifyPortalCode(body: { challengeToken: string; code: string }) {
  return api('POST', '/portal/sign-in/verify', { body });
}

export function portalSignOut() {
  return api('POST', '/portal/sign-out');
}

export function setPortalLanguage(language: UiLanguage) {
  return api('PUT', '/portal/me/language', { body: { language } });
}

function section<T>(patientId: string, name: string, schema: z.ZodType<T>) {
  return queryOptions({
    queryKey: portalKeys.section(patientId, name),
    queryFn: () => api('GET', `/portal/patients/${patientId}/${name}`, { schema }),
  });
}

export const portalAppointmentsQuery = (patientId: string) =>
  section(patientId, 'appointments', portalAppointmentsSchema);
export const portalBillsQuery = (patientId: string) =>
  section(patientId, 'bills', portalBillsSchema);
export const portalResultsQuery = (patientId: string) =>
  section(patientId, 'results', portalResultsSchema);
export const portalAllergiesQuery = (patientId: string) =>
  section(patientId, 'allergies', portalAllergiesSchema);
export const portalFeedbackQuery = (patientId: string) =>
  section(patientId, 'feedback', portalFeedbackSchema);

export function cancelPortalAppointment(patientId: string, appointmentId: string, reason: string) {
  return api('POST', `/portal/patients/${patientId}/appointments/${appointmentId}/cancel`, {
    body: reason.trim() === '' ? {} : { reason },
  });
}

export function submitPortalFeedback(patientId: string, visitId: string, body: SubmitFeedbackBody) {
  return api('POST', `/portal/patients/${patientId}/visits/${visitId}/feedback`, { body });
}

export function openFeedbackLink(token: string) {
  return api('POST', '/feedback/link', { body: { token }, schema: feedbackLinkSchema });
}

export function submitFeedbackLink(token: string, body: SubmitFeedbackBody) {
  return api('POST', '/feedback/link/submit', { body: { token, ...body } });
}
